import { useSearchParams } from "react-router";

import type { ProductCondition } from "@/features/marketplace/types/product";
import type { SearchFilters, SortOption } from "@/features/marketplace/types/search";

const DEFAULT_SORT: SortOption = "newest";

function parseFilters(params: URLSearchParams): SearchFilters {
  const conditionParam = params.get("condition");
  const minPriceParam = params.get("minPrice");
  const maxPriceParam = params.get("maxPrice");
  const pageParam = params.get("page");

  return {
    q: params.get("q") ?? "",
    category: params.get("category"),
    minPrice: minPriceParam ? Number(minPriceParam) : null,
    maxPrice: maxPriceParam ? Number(maxPriceParam) : null,
    conditions: conditionParam ? (conditionParam.split(",") as ProductCondition[]) : [],
    sort: (params.get("sort") as SortOption | null) ?? DEFAULT_SORT,
    page: pageParam ? Number(pageParam) : 1,
  };
}

function serializeFilters(filters: SearchFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.category) params.set("category", filters.category);
  if (filters.minPrice != null) params.set("minPrice", String(filters.minPrice));
  if (filters.maxPrice != null) params.set("maxPrice", String(filters.maxPrice));
  if (filters.conditions.length > 0) params.set("condition", filters.conditions.join(","));
  if (filters.sort !== DEFAULT_SORT) params.set("sort", filters.sort);
  if (filters.page > 1) params.set("page", String(filters.page));
  return params;
}

/**
 * The URL is the source of truth for search state (docs/ui/routes.md: `/search`
 * takes `q`, `category`, `minPrice`, `maxPrice`, `condition`, `sort`, `page`
 * as query params) — not component state — so a search is shareable/
 * bookmarkable/back-button-able for free. `updateFilters` resets `page` to 1
 * on any change that isn't itself a page change, so switching a filter
 * never leaves the user stranded on a now-out-of-range page.
 */
export function useSearchFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseFilters(searchParams);

  function updateFilters(partial: Partial<SearchFilters>) {
    const next: SearchFilters = { ...filters, ...partial };
    if (!("page" in partial)) {
      next.page = 1;
    }
    setSearchParams(serializeFilters(next));
  }

  function clearFilters() {
    updateFilters({ category: null, minPrice: null, maxPrice: null, conditions: [] });
  }

  return { filters, updateFilters, clearFilters };
}
